import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Printer, ArrowLeft, Loader2, Mail } from "lucide-react";
import { adminGet, isLoggedIn } from "../lib/adminAuth";
import { brand } from "../data/mock";

function fmt(n, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n || 0);
}

function buildMailto(q) {
  const to = q.client?.email || "";
  const subject = `Your quotation from Shobha with Shubhra — ${q.number}`;
  const clientName = (q.client?.name || "there").split(" ")[0];
  const eventLine = q.event?.event_type
    ? `${q.event.event_type}${q.event?.date ? ` on ${new Date(q.event.date).toLocaleDateString()}` : ""}`
    : "your celebration";
  const total = fmt(q.totals?.total || 0, q.currency || "USD");
  const deposit = fmt(q.totals?.deposit || 0, q.currency || "USD");
  const body = [
    `Dear ${clientName},`,
    "",
    `Thank you so much for the opportunity to design ${eventLine}. It would be a true joy to bring your vision to life.`,
    "",
    `Please find your personalised quotation (${q.number}) attached to this email.`,
    `Total investment: ${total}`,
    `Deposit to confirm booking: ${deposit}`,
    "",
    "I'd love to walk you through every detail on a call — please reply with a time that suits you, or let me know if you'd like to book a consultation.",
    "",
    "Warm regards,",
    "Shubhra Prasad",
    "Shobha with Shubhra",
    `${brand.email} · ${brand.phone}`,
    brand.location,
  ].join("\n");
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function QuotationPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isLoggedIn()) { nav("/admin/login"); return; }
    (async () => {
      try { setQ(await adminGet(`/admin/quotations/${id}`)); }
      catch (err) { setError(err.message); }
    })();
  }, [id, nav]);

  const filledReferenceImages = useMemo(
    () => (q?.reference_images || []).filter((im) => im.url),
    [q]
  );

  if (!q) return <div className="min-h-screen flex items-center justify-center">{error || <Loader2 className="animate-spin" />}</div>;
  const currency = q.currency || "USD";
  const t = q.totals || { subtotal: 0, discount: 0, tax: 0, total: 0, deposit: 0, balance: 0 };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg-alt))] print:bg-white">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-page { max-width: 100% !important; box-shadow: none !important; border: none !important; margin: 0 !important; padding: 32pt !important; }
        }
        @page { size: A4; margin: 12mm; }
      `}</style>

      <div className="no-print bg-[hsl(var(--bg))] border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1000px] px-6 h-16 flex items-center justify-between">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase"><ArrowLeft size={14} /> Back</button>
          <div className="flex items-center gap-3">
            <a
              href={buildMailto(q)}
              className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] px-4 py-2 text-[11px] tracking-[0.3em] uppercase transition-colors"
              title={q.client?.email ? `Send to ${q.client.email}` : "Client email is empty — will open your mail app without a recipient"}
            >
              <Mail size={14} /> Email to client
            </a>
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] px-4 py-2 text-[11px] tracking-[0.3em] uppercase transition-colors">
              <Printer size={14} /> Print / Save PDF
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-[1000px] px-6 pb-3 text-[11px] text-[hsl(var(--ink-soft))]">
          Tip: click <strong>Print / Save PDF</strong> first, then <strong>Email to client</strong> — your mail app opens pre-filled and you can drag the saved PDF into it.
        </div>
      </div>

      <div className="print-page mx-auto max-w-[1000px] my-8 bg-white shadow-lg p-12 border border-[hsl(var(--line))] text-black">
        {/* Header */}
        <div className="flex justify-between items-start pb-8 border-b border-neutral-300">
          <div className="flex items-start gap-4">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/v1a0y2ji_Shobhawithshubhra-logo-final.png"
              alt="Shobha with Shubhra"
              className="h-20 w-auto object-contain"
            />
            <div>
              <div className="font-editorial text-4xl leading-none">Shobha with Shubhra</div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-neutral-500 mt-2">Design · Planning · Decoration</div>
              <div className="text-xs text-neutral-600 mt-4">{brand.location}</div>
              <div className="text-xs text-neutral-600">{brand.email} · {brand.phone}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.4em] uppercase text-neutral-500">Quotation</div>
            <div className="font-display text-3xl mt-1">{q.number}</div>
            <div className="text-xs text-neutral-600 mt-2">Issued {new Date(q.created_at).toLocaleDateString()}</div>
            <div className="text-xs uppercase tracking-widest mt-1">{q.status}</div>
          </div>
        </div>

        {/* Client + Event */}
        <div className="grid grid-cols-2 gap-8 mt-8">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">Prepared for</div>
            <div className="font-display text-xl mt-2">{q.client?.name || "—"}</div>
            <div className="text-sm mt-1">{q.client?.email}</div>
            <div className="text-sm">{q.client?.phone}</div>
            <div className="text-sm text-neutral-600 mt-1">{q.client?.address}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">Event</div>
            <div className="font-display text-xl mt-2">{q.event?.event_type || "—"}</div>
            <div className="text-sm mt-1">{q.event?.date && new Date(q.event.date).toLocaleDateString()}</div>
            <div className="text-sm">{q.event?.venue} {q.event?.city ? `· ${q.event.city}` : ""}</div>
            <div className="text-sm text-neutral-600">{q.event?.guests ? `${q.event.guests} guests` : ""}</div>
          </div>
        </div>

        {q.services && q.services.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">Services included</div>
            <div className="mt-2 text-sm">{q.services.join(" · ")}</div>
          </div>
        )}

        {q.event?.notes && (
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">Requirements</div>
            <div className="mt-2 text-sm whitespace-pre-wrap">{q.event.notes}</div>
          </div>
        )}

        {/* Line items */}
        <div className="mt-10">
          <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-3">Investment breakdown</div>
          <table className="w-full text-sm border-t border-neutral-300">
            <thead className="text-left text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              <tr><th className="py-3">Description</th><th className="py-3 text-right w-16">Qty</th><th className="py-3 text-right w-32">Unit</th><th className="py-3 text-right w-32">Amount</th></tr>
            </thead>
            <tbody>
              {(q.line_items || []).map((li, i) => (
                <tr key={li._key || `${li.description}-${i}`} className="border-t border-neutral-200">
                  <td className="py-3">{li.description}</td>
                  <td className="py-3 text-right">{li.quantity}</td>
                  <td className="py-3 text-right">{fmt(li.unit_price, currency)}</td>
                  <td className="py-3 text-right">{fmt((li.quantity || 0) * (li.unit_price || 0), currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-8 flex justify-end">
          <div className="w-80 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>{fmt(t.subtotal, currency)}</span></div>
            {t.discount > 0 && <div className="flex justify-between"><span className="text-neutral-500">Discount</span><span>− {fmt(t.discount, currency)}</span></div>}
            <div className="flex justify-between"><span className="text-neutral-500">Tax ({q.tax_percent}%)</span><span>{fmt(t.tax, currency)}</span></div>
            <div className="flex justify-between font-display text-2xl pt-3 border-t border-neutral-300"><span>Total</span><span>{fmt(t.total, currency)}</span></div>
            <div className="flex justify-between pt-2 text-neutral-700"><span>Deposit due ({q.deposit_percent}%)</span><span>{fmt(t.deposit, currency)}</span></div>
            <div className="flex justify-between text-neutral-500"><span>Balance</span><span>{fmt(t.balance, currency)}</span></div>
          </div>
        </div>

        {/* Reference photos */}
        {filledReferenceImages.length > 0 && (
          <div className="mt-10">
            <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-3">Reference & inspiration</div>
            <div className="grid grid-cols-3 gap-3">
              {filledReferenceImages.map((im, i) => (
                <div key={im._key || `${im.url}-${i}`}>
                  <img src={im.url} alt={im.caption} className="w-full aspect-square object-cover" />
                  {im.caption && <div className="text-xs text-neutral-600 mt-1">{im.caption}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terms */}
        {q.terms && (
          <div className="mt-10 pt-6 border-t border-neutral-300">
            <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500 mb-2">Terms & conditions</div>
            <div className="text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap">{q.terms}</div>
          </div>
        )}

        <div className="mt-16 pt-6 border-t border-neutral-300 text-center text-[10px] tracking-[0.35em] uppercase text-neutral-500">
          Thank you — Shobha with Shubhra
        </div>
      </div>
    </div>
  );
}
